/**
 * <T k="nav.explore" /> — interface text that a superadmin can edit in place.
 *
 * Renders exactly what {t('nav.explore')} used to render: the bundled string,
 * unchanged. Once a superadmin has edited that key, the stored text is shown
 * instead and its formatting (**bold**, *italic*, _underline_, links, line
 * breaks, bullets) is rendered.
 *
 * While edit mode is on the text is wrapped in a marked span so the editor
 * knows exactly which key was clicked. Outside edit mode nothing is wrapped,
 * so the page structure is identical for visitors.
 */

import { useRawText } from '@/lib/i18n';
import { renderRichText } from '@/lib/richText';
import { useUiTextStore } from '@/stores/uiTextStore';

interface TProps {
  /** The locale key, as used in ui/src/locales/en.ts */
  k: string;
  /** Shown when the key is missing from both locales. */
  fallback?: string;
}

export function T({ k, fallback }: TProps) {
  const { value, fromOverride } = useRawText(k, fallback);
  const editMode = useUiTextStore((s) => s.editMode);

  const content = fromOverride ? renderRichText(value) : value;

  if (!editMode) return <>{content}</>;

  return (
    <span className="artext-editable" data-artext-key={k}>
      {content}
    </span>
  );
}

export default T;
